import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import TitleWithBack from '../../components/common/TitleWithBack';

function ExamUpdate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    examDate: '',
    module: '',
    startTime: '',
    durationHours: ''
  });
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tooltip, setTooltip] = useState('');
  const [originalSchedule, setOriginalSchedule] = useState(null); // { examDate, startTime, durationHours }
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [lastReschedule, setLastReschedule] = useState(null); // { reason, at } - most recent prior change, admin/archiver only

  useEffect(() => {
    async function fetchExam() {
      setLoading(true);
      try {
        const examRes = await axios.get(`/api/exams/${id}`, {
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const exam = examRes.data;
        const loadedForm = {
          examDate: exam.examDate || '',
          module: exam.module?.id ? exam.module.id.toString() : '',
          startTime: exam.startTime || '',
          durationHours: exam.durationMinutes !== undefined && exam.durationMinutes !== null
          ? (exam.durationMinutes / 60).toFixed(2).toString()
          : ''
        };
        setForm(loadedForm);
        setOriginalSchedule(loadedForm);
        if (exam.rescheduleReason) {
          setLastReschedule({ reason: exam.rescheduleReason, at: exam.rescheduledAt });
        }
      } catch (err) {
        setError('Error fetching exam details.');
      }
      setLoading(false);
    }
    fetchExam();
  }, [id]);

  const scheduleChanged = originalSchedule && (
    form.examDate !== originalSchedule.examDate ||
    form.startTime !== originalSchedule.startTime ||
    form.durationHours !== originalSchedule.durationHours
  );

  useEffect(() => {
    async function fetchModules() {
      try {
        const modulesRes = await axios.get('/api/modules/active-exam-series', {
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        const modArray = Array.isArray(modulesRes.data)
          ? modulesRes.data
          : (modulesRes.data.data || []);
        setModules(modArray);
      } catch (err) {
        console.error('Error fetching modules', err);
      }
    }
    fetchModules();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setTooltip(''); // Clear any previous error messages
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTooltip('');

    if (scheduleChanged && !rescheduleReason.trim()) {
      setTooltip('Error: Please give a reason for changing the exam date/time.');
      return;
    }

    setLoading(true);

    const payload = {
      examDate: form.examDate,
      module: { id: parseInt(form.module, 10) },
      startTime: form.startTime,
      durationMinutes: form.durationHours ? Math.round(parseFloat(form.durationHours) * 60) : null,
      rescheduleReason: scheduleChanged ? rescheduleReason.trim() : undefined
    };

    try {
      await axios.put(`/api/exams/${id}`, payload, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setTooltip('Exam updated successfully!');
      setTimeout(() => navigate('/admin/exams/view'), 2000);
    } catch (err) {
      console.error('Error updating exam:', err);
      if (err.response && err.response.data && err.response.data.message) {
        setTooltip('Error: ' + err.response.data.message);
      } else {
        setTooltip('Error updating exam.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && !form.module) {
    return <div className="container mt-4"><p>Loading...</p></div>;
  }

  if (error) {
    return <div className="container mt-4"><p className="text-danger">{error}</p></div>;
  }

  return (
    <div className="container mt-2">
      <TitleWithBack title="Update Exam" />
      {lastReschedule && (
        <div className="alert alert-info">
          <strong>Last rescheduled:</strong> {lastReschedule.at ? new Date(lastReschedule.at).toLocaleString() : 'N/A'}
          <br />
          <strong>Reason:</strong> {lastReschedule.reason}
        </div>
      )}
      <div className="card shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Exam Date</label>
              <input 
                type="date" 
                name="examDate" 
                className="form-control"
                value={form.examDate}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Module</label>
              <select 
                name="module" 
                className="form-select"
                value={form.module}
                disabled
                readOnly
              >
                <option value="">Select Module</option>
                {modules.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.moduleName}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Start Time</label>
              <input
                type="time"
                name="startTime"
                className="form-control"
                value={form.startTime}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Duration (Hours)</label>
              <input
                type="number"
                name="durationHours"
                className="form-control"
                value={form.durationHours}
                onChange={handleChange}
                step="0.01"
                min="0.25"
                required
              />
               <small className="form-text text-muted">
                For example 1.00 = 60 minutes,  1.25 = 75 minutes, 1.5 = 90 minutes
               </small>
            </div>
            {scheduleChanged && (
              <div className="mb-3">
                <label className="form-label">Reason for changing the date/time</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={rescheduleReason}
                  onChange={(e) => { setRescheduleReason(e.target.value); setTooltip(''); }}
                  placeholder="Why is the exam schedule being adjusted?"
                  required
                />
                <small className="form-text text-muted">
                  Visible to the archiver and admin. The examiner will only see the updated date/time, not this reason.
                </small>
              </div>
            )}
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Exam'}
            </button>
          </form>
          
          {tooltip && (
            <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' : 'alert-success'}`}>
              {tooltip}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExamUpdate;
